import { queryOptions } from '@tanstack/react-query';
import { sub } from 'date-fns';
import { getAggregationByProperty, getRetention } from './widgets-service';
import type { WidgetsHttpClient } from '../widgets-context';
import type { EventData, FilterGroup } from '../types';

// Query keys intentionally match apps/admin/src/hooks/query-options.ts so the
// admin route loaders can prefetch into the same cache entries the widgets
// read from.

const STALE_TIME_DATA = 1000 * 60; // 1 minute

export const aggregationByPropertyOptions = ({
  api,
  projectId,
  dateRange,
  eventName,
  property,
  filterGroups,
  transformData,
  transformKey,
}: {
  api: WidgetsHttpClient;
  projectId: string;
  dateRange: { startDate?: Date; endDate?: Date };
  eventName?: string;
  property?: string;
  filterGroups?: FilterGroup[];
  transformData?: (data: EventData[]) => Promise<EventData[]> | EventData[];
  transformKey?: string;
}) => {
  const isField = property?.[0] === '_';
  const searchField = isField ? property?.slice(1) : property;
  return queryOptions({
    queryKey: [
      'aggregation_per_property',
      projectId,
      dateRange,
      eventName,
      property,
      filterGroups,
      ...(transformKey ? [transformKey] : []),
    ],
    queryFn: async () => {
      const data = await getAggregationByProperty(api, {
        projectId,
        eventName,
        ...(isField && { field: searchField }),
        ...(!isField && { metadataField: property }),
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        filterGroups,
      });
      return transformData ? await transformData(data) : data;
    },
  });
};

export const retentionOptions = ({
  api,
  projectId,
  days,
  filterGroups,
}: {
  api: WidgetsHttpClient;
  projectId: string;
  days: number;
  filterGroups?: FilterGroup[];
}) =>
  queryOptions({
    queryKey: ['retention', projectId, days, filterGroups],
    queryFn: () =>
      getRetention(api, {
        projectId,
        startDate: sub(new Date(), { days }),
        filterGroups,
      }),
    staleTime: STALE_TIME_DATA,
    refetchOnMount: 'always' as const,
  });
